import { SyncService } from "frontend/services/syncService";
import { useSortedFrames } from "frontend/subscriptions";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Layer, Rect, Stage } from "react-konva";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { CanvasElement } from "../canvas-element";
import style from "./canvas-presentation-view.module.css";
import { useDebounce, useElementSize } from "usehooks-ts";
import { CloseIcon, FrameIcon } from "frontend/ui-components/svg-shapes";
import { FramesList } from "../frames-list";
import useStateValue from "frontend/state/value";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import { useBoardValue } from "frontend/state/board-state";
import { CanvasProvider } from "../canvas-context";
import { noop } from "frontend/utils/fn-utils";
import { getPathPrefix } from "../../utils/getPathPrefix";
import { getElementTypeForId } from "../elements/canvas-elements-utils";
import { BoardContext, getElementProvider } from "elements/index";
import ElementComponent from "elements/base/konva-component";
import { IntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import Konva from "konva";
import { MondayItemManagerProvider } from "frontend/canvas-designer-new/integrations/components/monday-item-manager-context";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import cn from "classnames";
import ChevronIcon from "frontend/ui-components/design-system/icons/chevron";
import { isCIM } from "utils/url-utils";

export default function CanvasPresentationView({
  syncService,
  onDismiss,
}: {
  syncService: SyncService<RW> | null;
  onDismiss: () => void;
}) {
  const [currentFrameIndex, setCurrentFrameIndex] = useState<number>(0);
  const { elements, sortedFrames, framesMap, hiddenFrameIds } = useSortedFrames(syncService?.getReplicache());
  const visibleFrames = sortedFrames.filter((f) => hiddenFrameIds.indexOf(`frame-${f.id}`) === -1);
  const isPresentationModeEnabledFF = useFeatureFlag("cim-presentation-mode");
  const isPresentationModeEnabled = isPresentationModeEnabledFF && isCIM();

  const stageRef = useRef<any>(null);
  const [stageContainerRef, _stageSize] = useElementSize();
  const [stageOffset, setStageOffset] = useState({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState(1);
  const [stageBackground, setStageBackground] = useState("#FFFFFF");
  const [showFramesList, setShowFramesList] = useState(false);
  const stageSize = useDebounce(_stageSize, 200);
  const [appState, _] = useStateValue();
  const [{ board, pusherChannel }] = useBoardValue();
  // Store layer refs for each frame
  const frameLayersRef = useRef<{ [frameId: string]: Konva.Layer }>({});

  const onFrameSelected = useCallback(
    (id) => {
      const index = visibleFrames.findIndex((e) => `frame-${e.id}` === id);
      if (index >= 0) {
        setCurrentFrameIndex(index);
        setShowFramesList(false);
      }
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.CANVAS_ACTION,
        "changed_frame_from_frames_list",
        null,
        board?.accountId,
        appState.user?.id
      );
    },
    [visibleFrames]
  );

  useEffect(() => {
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.CANVAS_ACTION,
      "presentation_screen_showed",
      null,
      board?.accountId,
      appState.user?.id
    );
    return () => {
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.CANVAS_ACTION,
        "presentation_screen_dismissed",
        null,
        board?.accountId,
        appState.user?.id
      );
    };
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentFrameIndex, elements]);

  useEffect(() => {
    if (visibleFrames.length === 0) {
      return;
    }
    const frame = visibleFrames[currentFrameIndex];
    if (!frame) {
      return;
    }
    const position = { x: frame.value.x, y: frame.value.y };

    const fWidth = frame.value.width * (frame.value.scaleX ?? 1);
    const fHeight = frame.value.height * (frame.value.scaleY ?? 1);
    const scale = Math.min(stageSize.width / fWidth, stageSize.height / fHeight);

    // fix centered position with rendered width/height
    const rWidth = fWidth * scale;
    const rHeight = fHeight * scale;
    if (rWidth < stageSize.width) {
      const diff = stageSize.width - rWidth;
      position.x -= diff / 2 / scale;
    }
    if (rHeight < stageSize.height) {
      const diff = stageSize.height - rHeight;
      position.y -= diff / 2 / scale;
    }

    setStageOffset(position);
    setStageScale(scale);
    setStageBackground(frame.value.fill ?? "#FFFFFF");
  }, [currentFrameIndex, stageSize, elements]);

  function handleKeyDown(e: any) {
    if (["ArrowRight", "ArrowDown", "Enter", " "].includes(e.key)) {
      nextSlide();
    } else if (["ArrowLeft", "ArrowUp", "Backspace"].includes(e.key)) {
      prevSlide();
    } else if (e.key === "Escape") {
      onDismiss();
    }
  }

  function nextSlide() {
    setCurrentFrameIndex((index) => Math.min(visibleFrames.length - 1, index + 1));
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.CANVAS_ACTION,
      "next_frame_button_clicked",
      null,
      board?.accountId,
      appState.user?.id
    );
  }

  function prevSlide() {
    setCurrentFrameIndex((index) => Math.max(0, index - 1));
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.CANVAS_ACTION,
      "prev_frame_button_clicked",
      null,
      board?.accountId,
      appState.user?.id
    );
  }

  function renderPager() {
    if (visibleFrames.length === 0) {
      return null;
    }
    const frame = visibleFrames[currentFrameIndex];
    if (!frame) {
      return null;
    }

    if (isPresentationModeEnabled) {
      return (
        <div className={style.presentationModePager}>
          <div className={style.presentationModePagerTitle}>{frame.value.title}</div>
          <div className={style.presentationModePagerButtons}>
            <div className={style.presentationModePagerArrow} onClick={prevSlide}>
              <ChevronIcon color="#d9d9d9" style={{ transform: "rotate(270deg)" }} />
            </div>
            <span className={style.presentationModePagerCounter}>
              {currentFrameIndex + 1}/{visibleFrames.length}
            </span>
            <div className={style.presentationModePagerArrow} onClick={nextSlide}>
              <ChevronIcon color="#d9d9d9" style={{ transform: "rotate(90deg)" }} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={style.pager}>
        <span className={style.pagerTitle}>{frame.value.title}</span>
        <div className={style.pagerButtons}>
          <div className={style.pagerArrow} onClick={prevSlide}>
            <img src={getPathPrefix("/images/grey-chevron.png")} />
          </div>
          <span>
            {currentFrameIndex + 1} of {visibleFrames.length}
          </span>
          <div className={style.pagerArrow} onClick={nextSlide}>
            <img src={getPathPrefix("/images/grey-chevron.png")} style={{ transform: "rotate(180deg)" }} />
          </div>
        </div>
      </div>
    );
  }

  function renderElements(ids: string[]) {
    const isLoggedIn = !(!appState.user || appState.user.isGuest);
    const context: BoardContext = {
      documentId: board!.documentId,
      boardId: board!.id,
      reflect: syncService?.getReplicache() ?? null,
      isReadOnly: true,
      isLoggedIn,
      selectedElementIds: [],
      setSelectedIds: noop,
      setStageState: noop,
      hiddenElementIds: [],
      editingElementId: null,
      editingElementLinkId: null,
      undoRedoStack: undefined,
    };
    return ids.map((id) => {
      const type = getElementTypeForId(id);
      const provider = getElementProvider(type);
      const element = elements.find((el) => id.includes(el.id))?.value;

      if (provider) {
        if (provider.isFFEnabled()) {
          // if provider exists it means it support the new infra
          return (
            <ElementComponent key={id} id={id} elementData={element} type={type} context={context} onResize={noop} />
          );
        }
        return null;
      }
      return (
        <CanvasElement
          key={id}
          syncService={syncService}
          uniqueId={id}
          elementData={element}
          onResize={noop}
          onChangeElement={noop}
          isSelected={false}
          drawOutlineAroundElements={false}
          isEditing={false}
          isEditingLink={false}
          isFrameHighlighted={false}
          onElementsMutationEnded={noop}
          isSelectable={false}
          isReadOnly={true}
          patchCanvasElement={noop}
          patchAnything={noop}
          documentId={board!.documentId}
        />
      );
    });
  }

  function renderAllFrameLayers() {
    if (visibleFrames.length === 0) {
      return null;
    }

    return visibleFrames.map((frame, index) => {
      const frameId = `${frame.type}-${frame.id}`;
      const elementIds = framesMap[frameId] || [];
      const isCurrentFrame = index === currentFrameIndex;

      return (
        <Layer
          key={frameId}
          ref={(ref) => {
            if (ref) {
              frameLayersRef.current[frameId] = ref;
            }
          }}
          visible={isCurrentFrame}
        >
          {/* Background rect for the frame */}
          <Rect
            x={stageOffset.x}
            y={stageOffset.y}
            width={Math.max(stageSize.width, 1)}
            height={Math.max(stageSize.height - 2, 1)}
            scaleX={1 / stageScale}
            scaleY={1 / stageScale}
            fill={isCurrentFrame ? stageBackground : "#FFFFFF"}
            cornerRadius={isPresentationModeEnabled ? 10 : 0}
          />
          {/* Render all elements for this frame */}
          {renderElements(elementIds)}
        </Layer>
      );
    });
  }

  function renderContent() {
    if (visibleFrames.length === 0) {
      return renderEmptyState();
    }
    return (
      <>
        <div className={style.slide} ref={stageContainerRef}>
          <Stage
            ref={stageRef}
            offsetX={stageOffset.x}
            offsetY={stageOffset.y}
            width={Math.max(stageSize.width - 2, 1)}
            height={Math.max(stageSize.height - 2, 1)}
            scaleX={stageScale}
            scaleY={stageScale}
          >
            <CanvasProvider appState={appState} documentId={board?.documentId}>
              <IntegrationsProvider documentId={board!.documentId} pusherChannel={pusherChannel}>
                <MondayItemManagerProvider documentId={board!.documentId}>
                  {renderAllFrameLayers()}
                </MondayItemManagerProvider>
              </IntegrationsProvider>
            </CanvasProvider>
          </Stage>
        </div>
        {renderPager()}
      </>
    );
  }

  function renderEmptyState() {
    return <div className={style.emptyState}>No frames to present</div>;
  }

  return (
    <div className={cn(style.container, isPresentationModeEnabled && style.presentationModeContainer)}>
      <div className={cn(style.header, isPresentationModeEnabled && style.presentationModeHeader)}>
        {!isPresentationModeEnabled && (
          <div
            className={style.headerButton}
            onClick={() => {
              tracking.trackEvent(
                consts.TRACKING_CATEGORY.CANVAS_ACTION,
                "presentation_frames_list_button_clicked",
                null,
                board?.accountId,
                appState.user?.id
              );
              setShowFramesList(visibleFrames.length > 0);
            }}
          >
            <FrameIcon size={17} />
          </div>
        )}
        <div className={style.headerButton} onClick={onDismiss}>
          <CloseIcon
            scale={isPresentationModeEnabled ? 1 : 1.4}
            color={isPresentationModeEnabled ? "#7F869B" : "#848199"}
          />
        </div>
      </div>
      <div className={cn(style.content, isPresentationModeEnabled && style.presentationModeContent)}>
        {renderContent()}
      </div>
      {showFramesList && (
        <div className={style.framesList}>
          <MemoedFramesList
            syncService={syncService}
            editEnabled={false}
            onFrameSelected={onFrameSelected}
            menuEnabled={false}
            onFrameDeleted={() => {}}
            showHidden={false}
          />
        </div>
      )}
    </div>
  );
}

const MemoedFramesList = React.memo(FramesList);
